import React, { useState, useContext, useEffect, useRef } from "react";
import classNames from "classnames";

import lockedIcon from "../../images/icons/adengage/locked.svg";
import { formatNumber } from "utils/stringHelpers";
import Tippy from "@tippyjs/react";
import infoIcon from "../../images/icons/adengage/info.svg";
import infoFilledIcon from "../../images/icons/adengage/info-filled.svg";
import AdEngageButton from "components/ad_engage_button";
import { MixPanelContext } from "../../context/mixpanel_context";


import * as styles from "./styles.module.scss";


export interface ToplineSummaryProps {
  airings?: number;
};

export interface SummaryCardProps {
  title: string;
  value?: string | number;
  locked?: boolean;
  tooltip?: string;
  handleMouseEnter?: () => void;
  handleMouseLeave?: () => void;
};



export const InfoButton: React.FC<{children: React.ReactNode}> = (props) => {
  const [isActive, setIsActive] = useState(false);
  const mixpanel = useContext(MixPanelContext);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsActive(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [ setIsActive ]);

  return (
    <Tippy
      placement="top"
      theme="dark"
      visible={isActive}
      maxWidth={260}
      content={props?.children}
    >
      <button type="button" onClick={() => { setIsActive(!isActive); mixpanel.track("Tooltip Click - Topline Summary Info");}} className={styles.infoButtonContainer}>
        { <img src={isActive? infoFilledIcon : infoIcon} className={styles.infoIcon} /> }
      </button>
    </Tippy>
  );
};

const SummaryCard: React.FC<(SummaryCardProps)> = ({title, value, locked = false, tooltip, handleMouseEnter = () => {}, handleMouseLeave = () => {}}) => {

  let valueDiv = <div className={styles.summaryCardValue}>
    {value}
  </div>;
  if (locked) {
    valueDiv = <div className={styles.summaryCardLock} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <img src={lockedIcon} alt="locked" />
      </div>;
  } else if (value === null) {
    valueDiv = <div className={styles.summaryCardNoData}>
      No Data Available
    </div>;
  }

  return (
    <>
      <div className={classNames("flex-col", styles.summaryCardContainer)}>
        <div className={classNames("flex-row", styles.summaryCardTitle)}>
          {title}
          { tooltip && 
            <InfoButton >
              <div className={styles.infoButton}>{tooltip}</div>
            </InfoButton>
          }
        </div>
        { valueDiv }
      </div>
    </>
  )
};

const ToplineSummary: React.FC<(ToplineSummaryProps)> = (props) => {
  const { airings } = props;

  const summaryCardModal = (<div className={styles.summaryCardModal}>
    <div className={styles.summaryCardModalHeader}>
      More Performance Metrics available
    </div>
    <div className={styles.summaryCardModalContent}>
      Always-on TV capture 24/7, 365 - performance data available the next morning
    </div>
    <AdEngageButton label="Request Demo" gotoLink="/contact-us" href={"/contact-us"} extraClassNames={styles.modalDemoButton} />
    
  </div>);

  return (
    <div className={classNames("flex-col", styles.toplineSummaryContainer)}>
      <div className={classNames("flex-row", styles.toplineSummaryHeader)}>
        <div className={"flex-col"}>
          <div className={styles.toplineTitle}>
            Creatives
          </div>
          <div className={styles.toplineSubtitle}>
            Last 7 Days
          </div>
        </div>
        <div className={classNames("flex-col", styles.lastUpdated)}>
          {`Last Updated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        </div>
      </div>
        <div className={classNames("flex-row flex-wrap", styles.toplineSummaryCards)}>
          <SummaryCard title={"Airings"} value={airings >= 100 ? formatNumber(airings, { decPlaces: 1 }) : null} />
          <Tippy
            arrow={false}
            content={summaryCardModal}
            interactive={true}
            placement={"bottom"}
            max-width={300}
          >
            <div className={"flex-row flex-wrap"}>
              <SummaryCard title={"Impressions"} locked />
              <SummaryCard title={"Spend"} locked />
              <SummaryCard
                title={"Search Engagement Lift"}
                locked
                tooltip={"Search Engagement is EDO’s proprietary measure of the increase in online search activity for a brand or product in the minutes immediately following a TV ad airing."}
                />
              <SummaryCard
                title={"Creative Engagement Rate"}
                locked
                tooltip={"Creative Engagement shows the relative power of ad content in generating online search activity for the advertised brand by controlling for when and on what network the ad aired."}
              />
            </div>
          </Tippy>
      </div>
    </div>
  );
};

export default ToplineSummary;
